import { useMemo, useState, useCallback, useEffect } from 'react';
import type { Row } from '@tanstack/react-table';
import type { ColumnMetadata } from '../types';
import { parseSummaryCellValue } from '../components/data-display/utils/summaryCalculators';
import { formatGridCellDisplayValue } from '../components/data-display/utils/gridCellDisplayValue';

export type GroupBucket<T> = {
  key: string;
  label: string;
  fieldName: string;
  depth: number;
  sums: Record<string, number>;
  rows: Row<T>[];
  children: GroupBucket<T>[];
  totalCount: number;
};

function getGroupIndex(meta: ColumnMetadata): number | undefined {
  const m = meta as Record<string, unknown>;
  const idx = m['groupIndex'] ?? m['GroupIndex'];
  const n = typeof idx === 'number' ? idx : typeof idx === 'string' ? parseInt(idx, 10) : NaN;
  return isNaN(n) || n < 0 ? undefined : n;
}

export function getShowInGroupFooter(meta: ColumnMetadata): boolean {
  const gs = (meta as Record<string, unknown>)['groupSummaries'] as
    Record<string, unknown> | undefined;
  if (!gs) return false;
  const v = gs['showInGroupFooter'] ?? gs['ShowInGroupFooter'];
  return v === true;
}

function getSummaryFields(columnMetadata: ColumnMetadata[]): string[] {
  const fields: string[] = [];
  for (const meta of columnMetadata) {
    const hasSummary = (meta as Record<string, unknown>)['groupSummaries'] != null;
    if (hasSummary) {
      const field = (meta.dataField || meta.field || '') as string;
      if (field) fields.push(field);
    }
  }
  return fields;
}

function sumField<T>(rows: Row<T>[], field: string): number {
  return rows.reduce((acc, row) => acc + parseSummaryCellValue(row.getValue(field)), 0);
}

function buildNestedGroups<T>(
  rows: Row<T>[],
  groupFields: string[],
  depth: number,
  parentKey: string,
  summaryFields: string[],
  columnMetaMap: Map<string, ColumnMetadata>,
  lookupMaps?: Map<string, Record<string, string>>
): GroupBucket<T>[] {
  if (rows.length === 0 || groupFields.length === 0) return [];

  const [currentField, ...remainingFields] = groupFields;
  const isLeaf = remainingFields.length === 0;

  const bucketOrder: string[] = [];
  const bucketMap = new Map<string, Row<T>[]>();
  const bucketLabels = new Map<string, string>();

  for (const row of rows) {
    const raw = row.getValue(currentField);
    const colMeta = columnMetaMap.get(currentField);
    const lookupMap = lookupMaps?.get(currentField);
    const formattedLabel =
      raw != null && raw !== ''
        ? formatGridCellDisplayValue({
            value: raw,
            lookupMap,
            dataType: (colMeta as Record<string, unknown> | undefined)?.['dataType'] as
              string | undefined,
            format: (colMeta as Record<string, unknown> | undefined)?.['format'] as
              string | undefined,
          })
        : '';
    const label = raw != null && raw !== '' ? formattedLabel || String(raw) : '(blank)';
    const bucketKey = raw == null || raw === '' ? 'blank' : `${typeof raw}:${String(raw)}`;
    if (!bucketMap.has(bucketKey)) {
      bucketMap.set(bucketKey, []);
      bucketLabels.set(bucketKey, label);
      bucketOrder.push(bucketKey);
    }
    bucketMap.get(bucketKey)!.push(row);
  }

  return bucketOrder.map((bucketKey) => {
    const label = bucketLabels.get(bucketKey)!;
    const groupRows = bucketMap.get(bucketKey)!;
    const key = `${parentKey}§${currentField}=${bucketKey}`;

    const sums: Record<string, number> = {};
    for (const sf of summaryFields) {
      sums[sf] = sumField(groupRows, sf);
    }

    const children = isLeaf
      ? []
      : buildNestedGroups(
          groupRows,
          remainingFields,
          depth + 1,
          key,
          summaryFields,
          columnMetaMap,
          lookupMaps
        );

    return {
      key,
      label,
      fieldName: currentField,
      depth,
      sums,
      rows: isLeaf ? groupRows : [],
      children,
      totalCount: groupRows.length,
    } satisfies GroupBucket<T>;
  });
}

export function useGridGrouping<T>(
  allRows: Row<T>[],
  columnMetadata: ColumnMetadata[] | undefined,
  onGroupFieldsChange?: (groupFields: string[]) => void,
  lookupMaps?: Map<string, Record<string, string>>
) {
  const columnList = useMemo(() => columnMetadata ?? [], [columnMetadata]);

  const defaultGroupFields = useMemo(() => {
    const grouped: Array<{ field: string; index: number; caption: string }> = [];

    for (const meta of columnList) {
      const idx = getGroupIndex(meta);
      if (idx == null) continue;
      const field = ((meta.dataField || meta.field || '') as string).trim();
      if (!field) continue;
      const caption = ((meta as Record<string, unknown>)['caption'] as string) || field;
      grouped.push({ field, index: idx, caption });
    }

    grouped.sort((a, b) => a.index - b.index);
    return grouped;
  }, [columnList]);

  const groupingEnabled = defaultGroupFields.length > 0;

  const [groupFields, setGroupFields] = useState<string[]>(() =>
    (columnMetadata ?? [])
      .flatMap((meta) => {
        const idx = getGroupIndex(meta);
        if (idx == null) return [];
        const field = ((meta.dataField || meta.field || '') as string).trim();
        return field ? [{ field, index: idx }] : [];
      })
      .sort((a, b) => a.index - b.index)
      .map((g) => g.field)
  );

  useEffect(() => {
    onGroupFieldsChange?.(groupFields);
  }, [groupFields, onGroupFieldsChange]);

  const groupCaptions = useMemo(() => {
    const map = new Map<string, string>();
    for (const g of defaultGroupFields) map.set(g.field, g.caption);
    for (const meta of columnList) {
      const field = ((meta.dataField || meta.field || '') as string).trim();
      const caption = ((meta as Record<string, unknown>)['caption'] as string) || field;
      if (field && !map.has(field)) map.set(field, caption);
    }
    return map;
  }, [defaultGroupFields, columnList]);

  const summaryFields = useMemo(() => getSummaryFields(columnList), [columnList]);

  const groupedRows = useMemo<GroupBucket<T>[] | null>(() => {
    if (groupFields.length === 0) return null;

    const columnMetaMap = new Map<string, ColumnMetadata>();
    for (const meta of columnList) {
      const field = ((meta.dataField || meta.field || '') as string).trim();
      if (field) columnMetaMap.set(field, meta);
    }

    return buildNestedGroups<T>(
      allRows,
      groupFields,
      0,
      'root',
      summaryFields,
      columnMetaMap,
      lookupMaps
    );
  }, [allRows, groupFields, summaryFields, columnList, lookupMaps]);

  // ── Collapsed state + toggle ────────────────────────────────────────────
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());

  const toggleGroup = useCallback((key: string) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  }, []);

  const reorderGroup = useCallback((fromIdx: number, toIdx: number) => {
    setGroupFields((prev) => {
      const next = [...prev];
      const [item] = next.splice(fromIdx, 1);
      next.splice(toIdx, 0, item);
      return next;
    });
  }, []);

  const addGroupField = useCallback((field: string) => {
    setGroupFields((prev) => (prev.includes(field) ? prev : [...prev, field]));
  }, []);

  const removeGroupField = useCallback((field: string) => {
    setGroupFields((prev) => prev.filter((f) => f !== field));
  }, []);

  return {
    groupFields,
    groupedRows,
    groupCaptions,
    isGrouped: groupFields.length > 0,
    groupingEnabled,
    collapsedGroups,
    toggleGroup,
    reorderGroup,
    addGroupField,
    removeGroupField,
  };
}
